import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useLocation, useNavigate } from 'react-router';
import { mixed, object, string } from 'yup';
import { useQuery } from 'react-query';
import type { CaptureCustomAnswer, CleengCaptureQuestionField, CognitoPersonalDetailsFormData, PersonalDetailsFormData } from '@jwp/ott-common/types/account';
import { getModule } from '@jwp/ott-common/src/modules/container';
import { useConfigStore } from '@jwp/ott-common/src/stores/ConfigStore';
import AccountController from '@jwp/ott-common/src/controllers/AccountController';
import { modalURLFromLocation } from '@jwp/ott-ui-react/src/utils/location';
import { ACCESS_MODEL } from '@jwp/ott-common/src/constants';
import useForm, { type UseFormOnSubmitHandler } from '@jwp/ott-hooks-react/src/useForm';
import useOffers from '@jwp/ott-hooks-react/src/useOffers';

import PersonalDetailsForm from '../../../components/PersonalDetailsForm/PersonalDetailsForm';
import LoadingOverlay from '../../../components/LoadingOverlay/LoadingOverlay';
import { getUserProfileData } from '../../../../../common/src/helpers/AuthHelpers';
import { createUserProfile, getUserProfile } from './../../../../../common/src/services/integrations/CustomAccountService';
import { useAppConfig } from '@jwp/ott-hooks-react/src/useAppConfig';

const yupConditional = (required: boolean, message: string) => {
  return required ? string().required(message) : mixed().notRequired();
};

const PersonalDetails = () => {
  const accountController = getModule(AccountController);

  const navigate = useNavigate();
  const location = useLocation();
  const { t } = useTranslation('account');
  const accessModel = useConfigStore((s) => s.accessModel);
  const { data, isLoading } = useQuery('captureStatus', accountController.getCaptureStatus);
  const { mediaOffers } = useOffers();
  const { appConfig } = useConfigStore();
  const { isCognitoEnabled } = useAppConfig();
  const hasMediaOffers = mediaOffers.length > 0;
  const [questionValues, setQuestionValues] = useState<Record<string, string>>({});
  const [questionErrors, setQuestionErrors] = useState<Record<string, string>>({});

  const fields = useMemo(() => Object.fromEntries(data?.settings.map((item) => [item.key, item]) || []), [data]);
  const questions = useMemo(
    () => (data?.settings.filter((item) => !!(item as CleengCaptureQuestionField).question) as CleengCaptureQuestionField[]) || [],
    [data],
  );

  const nextStep = useCallback(() => {
    const hasOffers = accessModel === ACCESS_MODEL.SVOD || (accessModel === ACCESS_MODEL.AUTHVOD && hasMediaOffers);

    navigate(modalURLFromLocation(location, hasOffers ? 'choose-offer' : 'welcome'), { replace: true });
  }, [navigate, location, accessModel, hasMediaOffers]);

  useEffect(() => {
    if (data && (!data.isCaptureEnabled || !data.shouldCaptureBeDisplayed)) nextStep();

    if (data && questions) {
      setQuestionValues(Object.fromEntries(questions.map((question) => [question.key, ''])));
    }
  }, [data, nextStep, questions]);

  const initialValues: PersonalDetailsFormData = {
    firstName: '',
    lastName: '',
    birthDate: '',
    companyName: '',
    phoneNumber: '',
    address: '',
    address2: '',
    city: '',
    state: '',
    postCode: '',
    country: '',
  };

  const questionChangeHandler = (event: React.ChangeEvent<HTMLInputElement>) => {
    const value = event.target.type === 'checkbox' && !event.target.checked ? '' : event.target.value;

    setQuestionValues((current) => ({ ...current, [event.target.name]: value }));
  };

  const PersonalDetailSubmitHandler: UseFormOnSubmitHandler<PersonalDetailsFormData> = async (
    formData,
    { setErrors, setSubmitting, setValidationSchemaError, validate },
  ) => {
    const requiredMessage = t('personal_details.this_field_is_required');
    const schema = object().shape({
      firstName: yupConditional(!!fields.firstNameLastName?.required, requiredMessage),
      lastName: yupConditional(!!fields.firstNameLastName?.required, requiredMessage),
      address1: yupConditional(!!fields.address?.required, requiredMessage),
      address2: yupConditional(!!fields.address?.required, requiredMessage),
      postCode: yupConditional(!!fields.address?.required, requiredMessage),
      state: yupConditional(!!fields.address?.required, requiredMessage),
      city: yupConditional(!!fields.address?.required, requiredMessage),
      companyName: yupConditional(!!fields.companyName?.required, requiredMessage),
      birthDate: fields.birthDate?.required
        ? string()
            .required(requiredMessage)
            .matches(/\d{4}-\d{2}-\d{2}/, t('personal_details.birth_date_not_valid'))
        : mixed().notRequired(),
      phoneNumber: yupConditional(!!fields.phoneNumber?.required, requiredMessage),
    });

    const errors: Record<string, string> = {};

    questions.forEach((question) => {
      if (question.enabled && question.required && !questionValues[question.key]) {
        errors[question.key] = t('personal_details.this_field_is_required');
      }
    });

    setQuestionErrors(errors);

    // we have validation errors
    if (!validate(schema) || Object.keys(errors).length) {
      setSubmitting(false);
      setValidationSchemaError(true);
      return;
    }

    try {
      const removeEmpty = (obj: Record<string, unknown>) =>
        Object.fromEntries(
          Object.keys(obj)
            .filter((key) => obj[key] !== '')
            .map((key) => [key, obj[key]]),
        );
      const customAnswers = questions.map(
        (question) =>
          ({
            question: question.question,
            questionId: question.key,
            value: questionValues[question.key],
          } as CaptureCustomAnswer),
      );

      await accountController.updateCaptureAnswers(removeEmpty({ ...formData, customAnswers }));
      nextStep();
    } catch (error: unknown) {
      if (error instanceof Error) {
        setErrors({ form: error.message });
      }
    }

    setSubmitting(false);
  };

  const cognitoSubmitHandler: UseFormOnSubmitHandler<CognitoPersonalDetailsFormData> = async (
    formData,
    { setErrors, setSubmitting, setValidationSchemaError, validate },
  ) => {
    const requiredMessage = t('personal_details.this_field_is_required');
    let schemaShape: { [key: string]: any } = {};

    if (appConfig?.user_profile?.personal_details?.first_name_last_name?.enabled) {
      if (appConfig?.user_profile?.personal_details?.first_name_last_name?.required) {
        schemaShape.firstName = string().required(requiredMessage);
        schemaShape.lastName = string().required(requiredMessage);
      } else {
        schemaShape.firstName = string();
        schemaShape.lastName = string();
      }
    }

    if (appConfig?.user_profile?.personal_details?.company_name?.enabled) {
      if (appConfig?.user_profile?.personal_details?.company_name?.required) {
        schemaShape.companyName = string().required(requiredMessage);
      } else {
        schemaShape.companyName = string();
      }
    }

    if (appConfig?.user_profile?.personal_details?.address?.enabled) {
      if (appConfig?.user_profile?.personal_details?.address?.required) {
        schemaShape.address = string().required(requiredMessage);
      } else {
        schemaShape.address = string();
      }
    }

    if (appConfig?.user_profile?.personal_details?.phone_number?.enabled) {
      if (appConfig?.user_profile?.personal_details?.phone_number?.required) {
        schemaShape.phoneNumber = string().required(requiredMessage);
      } else {
        schemaShape.phoneNumber = string();
      }
    }

    if (appConfig?.user_profile?.personal_details?.birth_date?.enabled) {
      if (appConfig?.user_profile?.personal_details?.birth_date?.required) {
        schemaShape.birthDate = string().required(requiredMessage);
      } else {
        schemaShape.birthDate = string();
      }
    }

    const schema = object().shape(schemaShape);

    // we have validation errors
    if (!validate(schema) || Object.keys(errors).length) {
      setSubmitting(false);
      setValidationSchemaError(true);
      return;
    }

    const accessToken = localStorage.getItem('access_token');
    if (accessToken) {
      try {
        const userProfile = await createUserProfile(
          JSON.stringify(
            Object.entries({
              firstname: appConfig?.user_profile?.personal_details?.first_name_last_name?.enabled ? formData.firstName : undefined,
              lastname: appConfig?.user_profile?.personal_details?.first_name_last_name?.enabled ? formData.lastName : undefined,
              companyName: appConfig?.user_profile?.personal_details?.company_name?.enabled ? formData.companyName : undefined,
              address: appConfig?.user_profile?.personal_details?.address?.enabled ? formData.address : undefined,
              phoneNumber: appConfig?.user_profile?.personal_details?.phone_number?.enabled ? formData.phoneNumber : undefined,
              birthDate: appConfig?.user_profile?.personal_details?.birth_date?.enabled ? formData.birthDate : undefined,
            }).reduce((acc: any, [key, value]) => {
              if (value !== undefined) {
                acc[key] = value;
              }
              return acc;
            }, {}),
          ),
        );

        if (userProfile) {
          const result = await getUserProfile();
          if (result) {
            const emailId = String(localStorage.getItem('userEmail'));
            const token = localStorage.getItem('inplayer_token');
            if (token) {
              const parsedToken = JSON.parse(token);
              if (parsedToken?.token && parsedToken?.refreshToken) {
                await accountController.cognitoLogin(
                  getUserProfileData(
                    {
                      idToken: parsedToken.token,
                      refreshToken: parsedToken.refreshToken,
                      emailId,
                    },
                    result,
                  ),
                );
                navigate('/');
              } else {
                setErrors({ form: 'Token is invalid or missing.' });
              }
            } else {
              setErrors({ form: 'Authentication token is missing.' });
            }
          } else {
            setErrors({ form: 'Failed to fetch user profile.' });
          }
        } else {
          setErrors({ form: 'Failed to create user profile.' });
        }
      } catch (error: unknown) {
        if (error instanceof Error) {
          setErrors({ form: error.message });
        } else {
          setErrors({ form: 'An unknown error occurred.' });
        }
      }
    } else {
      setErrors({ form: 'Access token is missing.' });
    }

    setSubmitting(false);
  };

  const { setValue, handleSubmit, handleChange, values, errors, validationSchemaError, submitting } = useForm<PersonalDetailsFormData>({
    initialValues,
    onSubmit: isCognitoEnabled ? cognitoSubmitHandler : PersonalDetailSubmitHandler,
  });

  if (isLoading) {
    return (
      <div style={{ height: 400 }}>
        <LoadingOverlay inline />
      </div>
    );
  }

  return (
    <PersonalDetailsForm
      fields={fields}
      questions={questions}
      onQuestionChange={questionChangeHandler}
      questionValues={questionValues}
      questionErrors={questionErrors}
      onSubmit={handleSubmit}
      onChange={handleChange}
      setValue={setValue}
      values={values}
      errors={errors}
      validationError={validationSchemaError}
      submitting={submitting}
    />
  );
};

export default PersonalDetails;
